/**
 * Shared metric helpers for the CrUX Geography map + country lists — the
 * "view the map by Score / LCP / INP / CLS" toggle. The /crux/geo
 * response carries field_score + lcp/inp/cls p75 per country, so those
 * are the switchable views (FCP/TTFB aren't in the geo payload).
 */
import {
  type MetricBand,
  clsBand,
  fcpBand,
  formatCls,
  formatMs,
  formatScore,
  inpBand,
  lcpBand,
  scoreBand,
  ttfbBand,
} from '@/lib/format';

/** Structural shape shared by the geo (per-country) and page (per-URL)
 *  CrUX rows — both carry a field score + the three core-vital p75s. */
export interface CruxScoreRow {
  field_score: number | null;
  lcp_p75: number | null;
  inp_p75: number | null;
  cls_p75: number | null;
  ttfb_p75: number | null;
  fcp_p75: number | null;
}

export type GeoMetric = 'score' | 'lcp' | 'inp' | 'cls' | 'ttfb' | 'fcp';

export const GEO_METRICS: ReadonlyArray<{ key: GeoMetric; label: string }> = [
  { key: 'score', label: 'Score' },
  { key: 'lcp', label: 'LCP' },
  { key: 'inp', label: 'INP' },
  { key: 'cls', label: 'CLS' },
  { key: 'ttfb', label: 'TTFB' },
  { key: 'fcp', label: 'FCP' },
];

export function geoValueOf(c: CruxScoreRow, m: GeoMetric): number | null {
  switch (m) {
    case 'score':
      return c.field_score;
    case 'lcp':
      return c.lcp_p75;
    case 'inp':
      return c.inp_p75;
    case 'cls':
      return c.cls_p75;
    case 'ttfb':
      return c.ttfb_p75;
    case 'fcp':
      return c.fcp_p75;
  }
}

export function geoBandOf(c: CruxScoreRow, m: GeoMetric): MetricBand {
  switch (m) {
    case 'score':
      return scoreBand(c.field_score);
    case 'lcp':
      return lcpBand(c.lcp_p75);
    case 'inp':
      return inpBand(c.inp_p75);
    case 'cls':
      return clsBand(c.cls_p75);
    case 'ttfb':
      return ttfbBand(c.ttfb_p75);
    case 'fcp':
      return fcpBand(c.fcp_p75);
  }
}

export function geoFormatValue(c: CruxScoreRow, m: GeoMetric): string {
  const v = geoValueOf(c, m);
  if (m === 'score') return formatScore(v);
  if (m === 'cls') return formatCls(v);
  return formatMs(v);
}

/** Normalised "goodness" (higher = better) for a consistent best-first
 *  sort: field score is already higher-is-better; p75 metrics are
 *  lower-is-better, so negate. Missing values sink to the bottom. */
export function geoGoodness(c: CruxScoreRow, m: GeoMetric): number {
  const v = geoValueOf(c, m);
  if (v == null) return Number.NEGATIVE_INFINITY;
  return m === 'score' ? v : -v;
}

/** Coarse popularity rank (a magnitude bucket like 1000 / 1_000_000 —
 *  "this origin is in the top N most-visited") → a friendly band label.
 *  Product decision: show a band, not the raw "Top 1M" magnitude. */
export function rankBand(rank: number | null | undefined): string | null {
  if (rank == null || rank <= 0) return null;
  if (rank <= 1_000) return 'Extremely popular';
  if (rank <= 10_000) return 'Very popular';
  if (rank <= 100_000) return 'Popular';
  if (rank <= 1_000_000) return 'Moderately popular';
  if (rank <= 10_000_000) return 'Niche';
  return 'Long tail';
}

/** Raw rank magnitude → a compact "top N" label for the tooltip/detail
 *  (the band is the headline; this is the underlying number). */
export function rankMagnitude(rank: number | null | undefined): string | null {
  if (rank == null || rank <= 0) return null;
  if (rank >= 1_000_000) return `top ${rank / 1_000_000}M`;
  if (rank >= 1_000) return `top ${rank / 1_000}K`;
  return `top ${rank}`;
}

/** Band → a 0..1 "quality" proportion so the list bar wash reads as
 *  "longer = better" for every metric, not just the field score. */
export function bandQuality(b: MetricBand): number {
  switch (b) {
    case 'good':
      return 1;
    case 'needs-improvement':
      return 0.55;
    case 'poor':
      return 0.28;
    default:
      return 0;
  }
}
